import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
} from '@mui/material';

/** cdnFolder: sub whose master_sheet owns the image (parent when viewer sub is a subscriber). */
function subredditDefaultLogoUrl(cdnFolder: string, imageSource: string | null | undefined): string | null {
  if (!imageSource) return null;
  const cdnBaseUrl = import.meta.env.VITE_CDN_BASE_URL || 'https://cdn.redditcfb.com';
  const imagePath = import.meta.env.VITE_CDN_IMAGE_PATH || '60x60';
  return `${cdnBaseUrl}/${imagePath}/${cdnFolder}/${imageSource}.png`;
}
import { Login as LoginIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getSubredditFromUrl,
  isSubdomainPattern,
  navigateOrExternal,
  subredditLandingUrl,
} from '../utils/subreddit';

interface PopularSubreddit {
  subreddit: string;
  userCount: number;
  defaultLogo?: string | null;
  /** master_sheet / CDN folder for default_logo stem (parent when subscribed to a main sub) */
  cdnSub?: string | null;
}

const LandingPage: React.FC = () => {
  const { user, login, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const [popularSubreddits, setPopularSubreddits] = useState<PopularSubreddit[]>([]);
  const [isLoadingPopular, setIsLoadingPopular] = useState(true);

  // If someone opens marketing URL on a tenant host by mistake, normalize to /
  useEffect(() => {
    if (isSubdomainPattern() && getSubredditFromUrl()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Load popular subreddits
  useEffect(() => {
    const loadPopularSubreddits = async () => {
      try {
        const { API_BASE_URL } = await import('../config');
        const response = await fetch(`${API_BASE_URL}/popular-subreddits`);
        if (response.ok) {
          const data = await response.json();
          setPopularSubreddits(data.subreddits || []);
        }
      } catch (error) {
        // Silently fail - show empty list
      } finally {
        setIsLoadingPopular(false);
      }
    };
    loadPopularSubreddits();
  }, []);

  const handleSubredditClick = (subreddit: string) => {
    navigateOrExternal(navigate, subredditLandingUrl(subreddit));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" color="primary" sx={{ flexShrink: 0 }}>
        <Toolbar>
          <Box
            component="img"
            src="/flairwizard.png"
            alt="Flair Wizard"
            sx={{
              height: 40,
              width: 40,
              mr: 2,
              objectFit: 'contain',
            }}
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Flair Wizard
          </Typography>
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Welcome, {user.username}
              </Typography>
              <Button
                color="inherit"
                startIcon={<LogoutIcon />}
                onClick={logout}
                size="small"
              >
                Logout
              </Button>
            </Box>
          ) : (
            <Button
              color="inherit"
              startIcon={<LoginIcon />}
              onClick={login}
              disabled={isLoading}
            >
              Login with Reddit
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
          <Box textAlign="center" sx={{ mb: 6 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: { xs: 1, sm: 2 }, 
                mb: 2 
              }}
            >
              <Box
                component="img"
                src="/flairwizard.png"
                alt="Flair Wizard"
                sx={{
                  height: { xs: 72, sm: 96, md: 120 },
                  width: { xs: 72, sm: 96, md: 120 },
                  objectFit: 'contain',
                }}
              />
              <Typography 
                variant="h2" 
                component="h1" 
                sx={{ 
                  mb: 0,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                }}
              >
                Flair Wizard
              </Typography>
            </Box>
            <Typography variant="h5" color="text.secondary" paragraph>
              The easiest way to manage and select Flair on Reddit.
            </Typography>
          </Box>

          <Box sx={{ mt: 6 }}>
            <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
              Popular Subreddits
            </Typography>
            {isLoadingPopular ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                  {popularSubreddits.map((item) => {
                    const logoUrl = subredditDefaultLogoUrl(item.cdnSub || item.subreddit, item.defaultLogo);
                    return (
                      <Button
                        key={item.subreddit}
                        variant="outlined"
                        onClick={() => handleSubredditClick(item.subreddit)}
                        sx={{ textTransform: 'none' }}
                        startIcon={
                          logoUrl ? (
                            <Box
                              component="img"
                              src={logoUrl}
                              alt=""
                              sx={{
                                width: 28,
                                height: 28,
                                objectFit: 'contain',
                                borderRadius: 0.5,
                              }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : undefined
                        }
                      >
                        /r/{item.subreddit}
                      </Button>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        </Container>
        
        {/* Logged-in only: contact + Discord OAuth (same as mod Settings → Connect Discord) */}
        {user && (
          <Box sx={{ p: 3, backgroundColor: 'grey.100', textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Contact bakonydraco to get Flair Wizard set up for your subreddit
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => {
                  const message = `I want to add Flair Wizard to [Fill in my subreddit]`;
                  const redditUrl = `https://www.reddit.com/message/compose/?to=bakonydraco&subject=Flair%20Wizard%20Request&message=${encodeURIComponent(message)}`;
                  window.open(redditUrl, '_blank');
                }}
              >
                PM bakonydraco
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={async () => {
                  try {
                    const { API_BASE_URL } = await import('../config');
                    const response = await fetch(`${API_BASE_URL}/moderator/discord-auth/initiate-landing`, {
                      method: 'GET',
                      headers: { Authorization: `Bearer ${user.username}` },
                    });
                    const data = await response.json();
                    if (!response.ok) {
                      throw new Error(data.error || data.details || 'Could not start Discord connection');
                    }
                    if (data.authUrl) {
                      window.location.href = data.authUrl;
                    }
                  } catch (e) {
                    console.error(e);
                    window.alert(
                      e instanceof Error ? e.message : 'Could not connect Discord. Try again from Manage Flair → Settings on your sub.'
                    );
                  }
                }}
              >
                Chat on Discord
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Uses the same Discord link as mod settings: if you moderate subs here, you get those channels; otherwise you join as a general member.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LandingPage;
